import { useEffect } from "react";
import "../../styles/Overlays/Statusmodal.css";

// ── STAGE DEFINITIONS ────────────────────────────────────────────────────────

const APP_STAGES = [
  { key: "submitted",    label: "Submitted",    icon: "📥", desc: "Application received. Awaiting initial review by Kole Medical." },
  { key: "under_review", label: "Under Review", icon: "🔍", desc: "Documents are being checked for completeness and correctness." },
  { key: "acknowledged", label: "Acknowledged", icon: "📌", desc: "SAHPRA has formally received and logged your application." },
  { key: "evaluation",   label: "Evaluation",   icon: "⚗️",  desc: "Technical evaluators are assessing your application against regulatory requirements." },
  { key: "approved",     label: "Approved",     icon: "✅", desc: "Application approved by SAHPRA. License is being prepared." },
  { key: "issued",       label: "Issued",       icon: "📜", desc: "Your official license certificate has been generated and issued." },
  { key: "completed",    label: "Completed",    icon: "🎉", desc: "Process complete. Your license has been delivered." },
];

const QUOTE_STAGES = [
  { key: "pending",  label: "Pending",      icon: "🕐", desc: "Your quotation request has been received and is in the queue to be reviewed." },
  { key: "accepted", label: "Acknowledged", icon: "📌", desc: "Kole Medical has reviewed your request and is preparing your quotation." },
  { key: "sent",     label: "Quote Sent",   icon: "📨", desc: "Your quotation has been sent. Check your email for the full breakdown and pricing." },
];

// ── TYPES ────────────────────────────────────────────────────────────────────

interface ApplicationData {
  id: string;
  applicantName: string;
  licenseType: string;
  status: string;
  dateSubmitted: string;
}

interface QuoteData {
  id: string;
  fullName: string;
  licenseType: string;
  status: string;
  createdAt: Date;
}

interface StatusModalProps {
  type: "application" | "quote";
  data: ApplicationData | QuoteData;
  onClose: () => void;
}

// ── HELPERS ──────────────────────────────────────────────────────────────────

function getStageIndex(stages: { key: string }[], status: string): number {
  return stages.findIndex((s) => s.key === status);
}

function getStepState(
  stageIndex: number,
  currentIndex: number,
  isRejected: boolean
): "completed" | "active" | "rejected" | "upcoming" {
  if (isRejected && stageIndex === currentIndex) return "rejected";
  if (stageIndex < currentIndex) return "completed";
  if (stageIndex === currentIndex) return "active";
  return "upcoming";
}

// ── COMPONENT ────────────────────────────────────────────────────────────────

export default function StatusModal({ type, data, onClose }: StatusModalProps) {
  const isApp = type === "application";
  const stages = isApp ? APP_STAGES : QUOTE_STAGES;
  const status = data.status;
  const isRejected = status === "rejected";

  // If rejected, show up to the evaluation stage (last real step before rejection)
  const displayStages = isRejected && isApp
    ? [...stages, { key: "rejected", label: "Rejected", icon: "❌", desc: "Application was declined. Please contact Kole Medical to discuss next steps or resubmission." }]
    : isRejected && !isApp
    ? [...stages, { key: "rejected", label: "Rejected", icon: "❌", desc: "Quotation was not accepted. Please contact Kole Medical for a revised quote." }]
    : stages;

  const currentIndex = getStageIndex(displayStages, status);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const progressPercent = currentIndex >= 0
    ? Math.round((currentIndex / (displayStages.length - 1)) * 100)
    : 0;

  const title = isApp
    ? `Application — ${(data as ApplicationData).id}`
    : `Quotation — ${(data as QuoteData).id}`;

  const subtitle = isApp
    ? `${(data as ApplicationData).licenseType} License · Submitted ${(data as ApplicationData).dateSubmitted}`
    : `${(data as QuoteData).licenseType} License`;

  return (
    <div className="sm-overlay" onClick={onClose}>
      <div className="sm-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── HEADER ── */}
        <div className={`sm-header sm-header-${isRejected ? "rejected" : isApp ? "app" : "quote"}`}>
          <div className="sm-header-content">
            <span className="sm-header-icon">{isApp ? "📄" : "💰"}</span>
            <div>
              <div className="sm-header-title">{title}</div>
              <div className="sm-header-subtitle">{subtitle}</div>
            </div>
          </div>
          <button className="sm-close" onClick={onClose}>✕</button>
        </div>

        {/* ── PROGRESS SUMMARY BAR ── */}
        <div className="sm-progress-summary">
          <div className="sm-progress-labels">
            <span className="sm-progress-text">
              Step {Math.max(currentIndex + 1, 1)} of {displayStages.length}
            </span>
            <span className={`sm-progress-percent ${isRejected ? "rejected" : ""}`}>
              {isRejected ? "Rejected" : `${progressPercent}%`}
            </span>
          </div>
          <div className="sm-progress-track">
            <div
              className={`sm-progress-fill ${isRejected ? "rejected" : ""}`}
              style={{ width: `${isRejected ? 100 : progressPercent}%` }}
            />
          </div>
        </div>

        {/* ── TIMELINE ── */}
        <div className="sm-body">
          <div className="sm-timeline">
            {displayStages.map((stage, i) => {
              const state = getStepState(i, currentIndex, isRejected);
              return (
                <div key={stage.key} className={`sm-step sm-step-${state}`}>

                  {/* Connector line */}
                  {i < displayStages.length - 1 && (
                    <div className={`sm-connector ${i < currentIndex ? "filled" : ""} ${isRejected && i === currentIndex - 1 ? "rejected" : ""}`} />
                  )}

                  {/* Circle */}
                  <div className="sm-circle-wrap">
                    <div className="sm-circle">
                      {state === "completed" && <span className="sm-check">✓</span>}
                      {state === "active"    && <span className="sm-pulse" />}
                      {state === "rejected"  && <span>✕</span>}
                      {state === "upcoming"  && <span className="sm-num">{i + 1}</span>}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="sm-step-content">
                    <div className="sm-step-header">
                      <span className="sm-step-icon">{stage.icon}</span>
                      <span className="sm-step-label">{stage.label}</span>
                      {state === "active" && !isRejected && (
                        <span className="sm-step-badge">Current</span>
                      )}
                      {state === "rejected" && (
                        <span className="sm-step-badge rejected">Rejected</span>
                      )}
                    </div>
                    <p className="sm-step-desc">{stage.desc}</p>
                  </div>

                </div>
              );
            })}
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div className="sm-footer">
          <p className="sm-footer-note">
            📞 Questions about your {isApp ? "application" : "quotation"}? Contact{" "}
            <a href="mailto:info@kolemedical.co.za">info@kolemedical.co.za</a>
          </p>
          <button className="sm-close-btn" onClick={onClose}>
            Close
          </button>
        </div>

      </div>
    </div>
  );
}